import { Keys } from '@paperbits/common/keyboard';
import { Events, MouseButton } from '@paperbits/common/events/commonEvents';
import { AriaAttributes, DataAttributes } from '@paperbits/common/html';
import { clickTracker } from './tracking';


const showClassName = 'show';
const popupContainerClass = 'popup-container';
const onPopupRepositionRequestedEvent = 'onPopupRepositionRequested';
const onPopupRequestedEvent = 'onPopupRequested';

const dataDismissOutside = 'data-dismiss-outside';

const modalAnimationDuration = 300;

declare global {
    interface Window {
        OneTrust?: {
            ToggleInfoDisplay: () => void;
        };
    }
}

export enum TriggerEvent {
    Click = 'click',
    Hover = 'hover',
    Focus = 'focus',
    KeyDown = 'keydown'
}
interface ToggleableHandle {
    close(): void;
}

export const openTogglable = (toggleElement: HTMLElement, toggleType: string, triggerEvent: TriggerEvent): ToggleableHandle => {
    switch (toggleType) {
        case 'popup': {
            const targetSelector = toggleElement.getAttribute(DataAttributes.Target);

            if (!targetSelector) {
                return;
            }

            const targetElement = <HTMLElement>document.querySelector(targetSelector);

            if (!targetElement) {
                return;
            }

            return onShowPopup(toggleElement, targetElement, triggerEvent);
        }

        case 'dropdown': {
            let nextSibling = toggleElement.nextElementSibling;
            while (nextSibling && !nextSibling.classList.contains('dropdown')) {
                nextSibling = nextSibling.nextElementSibling;
            }
            const targetElement = <HTMLElement>(nextSibling || toggleElement.parentElement);
            const dismissOutside = toggleElement.getAttribute(dataDismissOutside);
            const dontCloseOnClickOutside = dismissOutside && dismissOutside === 'false';
            return onShowTogglable(toggleElement, targetElement, !dontCloseOnClickOutside);
        }

        case 'collapsible': {
            const targetElement = <HTMLElement>toggleElement.closest('.collapsible-panel');
            const dismissOutside = toggleElement.getAttribute(dataDismissOutside);
            const dontCloseOnClickOutside = dismissOutside && dismissOutside === 'false';
            return onShowTogglable(toggleElement, targetElement, !dontCloseOnClickOutside);
        }

        case 'modal': {
            const parentElement = <HTMLElement>toggleElement.parentElement;
            const targetElement = parentElement.querySelector<HTMLElement>('.modal');
            return onShowModal(toggleElement, targetElement);
        }

        case 'modalvideo': {
            const parentElement = <HTMLElement>toggleElement.parentElement;
            const targetElement = parentElement.querySelector<HTMLElement>('.modal');
            const videoElement = targetElement.querySelector<HTMLElement>('.video-dam-container');
            videoElement.innerHTML = videoElement.getAttribute('data-player-frame');
            return onShowModal(toggleElement, targetElement);
        }

        case 'playpause': {
            const parentElement = <HTMLElement>toggleElement.parentElement;
            const targetElement = parentElement.querySelector<HTMLIFrameElement>('.video-dam-container iframe');
            const status = toggleElement.getAttribute('data-video-status');
            if (status !== 'pause') {
                targetElement.contentWindow.postMessage('PAUSE', '*');
                toggleElement.setAttribute('data-video-status', 'pause');
            } else {
                targetElement.contentWindow.postMessage('PLAY', '*');
                toggleElement.setAttribute('data-video-status', 'play');
            }
            return;
        }

        case 'cookiepref': {
            if (window.OneTrust) {
                window.OneTrust.ToggleInfoDisplay();
            }
            return;
        }

        default:
            console.warn(`Unknown data-toggle value ${toggleType}`);
    }
};

export const onClick = (event: MouseEvent): void => {
    if (event.button !== MouseButton.Main) {
        return;
    }

    const eventTarget = <HTMLElement>event.target;
    const toggleElement = <HTMLElement>eventTarget.closest(`[${DataAttributes.Toggle}]`);

    if (toggleElement) {
        event.preventDefault();

        const toggleType = toggleElement.getAttribute(DataAttributes.Toggle);
        openTogglable(toggleElement, toggleType, TriggerEvent.Click);

    } else {
        clickTracker(eventTarget);
    }
};

export const onMouseEnter = (event: MouseEvent): void => {
    const eventTarget = <HTMLElement>event.target;
    const toggleElement = <HTMLElement>eventTarget.closest(`[${DataAttributes.Toggle}]`);

    if (!toggleElement) {
        return;
    }

    const triggerEvent = toggleElement.getAttribute(DataAttributes.TriggerEvent);

    /* if (triggerEvent !== TriggerEvent.Hover) {
        return;
    } */
    if (triggerEvent === TriggerEvent.Hover) {

        event.preventDefault();

        const toggleType = toggleElement.getAttribute(DataAttributes.Toggle);
        openTogglable(toggleElement, toggleType, TriggerEvent.Hover);
    }
};


const onKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode !== Keys.Enter && event.keyCode !== Keys.Space) {
        return;
    }
};

export const onShowTogglable = (toggleElement: HTMLElement, targetElement: HTMLElement, closeOnClickOutside = true, focusInside = true): ToggleableHandle => {
    if (!toggleElement || !targetElement) {
        return;
    }

    let dismissElement = targetElement.querySelector<HTMLElement>(`[${DataAttributes.Dismiss}]`);
    const dismissControls = dismissElement?.getAttribute('aria-controls')
    if (dismissControls && dismissControls !== targetElement.id) {
        dismissElement = undefined; // remove if for nested dropdown
    }

    const openTarget = (): void => {
        toggleElement.setAttribute(AriaAttributes.expanded, 'true');
        targetElement.classList.add(showClassName);
        targetElement.setAttribute(AriaAttributes.hidden, 'false');
        if (focusInside) { // focus the first visible element inside dropdown after it becomes visible
            setTimeout(() => {
                const interactive = Array.from(targetElement.querySelectorAll<HTMLElement>('a, button, input'))
                const first = interactive.find(i => {
                    const style = window.getComputedStyle(i)
                    return style.display !== "none" && style.visibility !== "hidden"
                })
                first?.focus()
            }, 300)
        }

        if (closeOnClickOutside) {
            setImmediate(() => addEventListener(Events.MouseDown, clickOutside));
        }
        if (dismissElement) {
            dismissElement.addEventListener(Events.MouseDown, closeTarget);
            dismissElement.addEventListener(Events.KeyDown, closeTarget);
        }

        toggleElement.dispatchEvent(new CustomEvent('togglable-open', {
            bubbles: true,
            detail: {
                target: targetElement
            }
        }));
    };

    const closeTarget = (e?: MouseEvent | KeyboardEvent): void => {
        if (e && 'key' in e && e.key !== 'Enter') { return; }

        targetElement.classList.remove(showClassName);
        targetElement.setAttribute(AriaAttributes.hidden, 'true');
        toggleElement.setAttribute(AriaAttributes.expanded, 'false');

        if (closeOnClickOutside) {
            removeEventListener(Events.MouseDown, clickOutside);
        }
        if (dismissElement) {
            dismissElement.removeEventListener(Events.MouseDown, closeTarget);
            dismissElement.removeEventListener(Events.KeyDown, closeTarget);
        }

        toggleElement.dispatchEvent(new CustomEvent('togglable-close', {
            bubbles: true,
            detail: {
                target: targetElement
            }
        }));
    };

    const clickOutside = (event: MouseEvent) => {
        const clickTarget = <HTMLElement>event.target;

        if (clickTarget.nodeName === 'BODY') {
            return;
        }

        const isTargetClicked = targetElement.contains(clickTarget);
        const isToggleClicked = toggleElement.contains(clickTarget);

        if (isTargetClicked || isToggleClicked) {
            return;
        }

        closeTarget();
    };

    if (!targetElement.classList.contains(showClassName)) {
        openTarget();
    } else {
        closeTarget();
    }

    return {
        close: closeTarget
    };
};

export const onShowPopup = (toggleElement: HTMLElement, targetElement: HTMLElement, triggerEvent: TriggerEvent): ToggleableHandle => {
    if (!toggleElement || !targetElement) {
        return;
    }

    const isTargetOpen = (): boolean => {
        return targetElement.classList.contains(showClassName);
    };

    if (isTargetOpen()) {
        return;
    }

    const popupContainerElement: HTMLElement = targetElement.querySelector(`.${popupContainerClass}`);

    const repositionPopup = (event?: CustomEvent): void => {
        const computedStyles = getComputedStyle(popupContainerElement);

        if (computedStyles.position === 'absolute') {
            const actualToggleElement: HTMLElement = event?.detail?.element || toggleElement;
            const toggleElementRect = actualToggleElement.getBoundingClientRect();
            const popupContainerElement: HTMLElement = targetElement.querySelector(`.${popupContainerClass}`);
            const popupContainerElementRect = popupContainerElement.getBoundingClientRect();
            const requestedPosition = actualToggleElement.getAttribute('data-position') || 'bottom';

            const triggerHalfWidth = Math.floor(toggleElementRect.width / 2);
            const triggerHalfHeight = Math.floor(toggleElementRect.height / 2);
            const popupHalfWidth = Math.floor(popupContainerElementRect.width / 2);
            const popupHalfHeight = Math.floor(popupContainerElementRect.height / 2);

            const position = requestedPosition.split(' ');

            // Default assignments
            popupContainerElement.style.left = `${toggleElementRect.left + triggerHalfWidth - popupHalfWidth}px`;
            popupContainerElement.style.top = `${window.scrollY + toggleElementRect.top + triggerHalfHeight - popupHalfHeight}px`;

            if (position.includes('top')) {
                popupContainerElement.style.top = `${window.scrollY + toggleElementRect.top - popupContainerElementRect.height - triggerHalfHeight}px`;
            }

            if (position.includes('bottom')) {
                popupContainerElement.style.top = `${window.scrollY + toggleElementRect.bottom}px`;
            }

            if (position.includes('left')) {
                popupContainerElement.style.left = `${toggleElementRect.left}px`;
            }

            if (position.includes('right')) {
                popupContainerElement.style.left = `${toggleElementRect.right - popupContainerElementRect.width}px`;
            }

            return;
        }

        popupContainerElement.removeAttribute('style');
    };

    const dismissElements: HTMLElement[] = Array.from(targetElement.querySelectorAll<HTMLElement>(`[${DataAttributes.Dismiss}]`));


    const checkOutside = (event: MouseEvent) => {
        const eventTarget = <HTMLElement>event.target;

        if (eventTarget.nodeName === 'BODY') {
            return;
        }

        const isTargetClicked = popupContainerElement.contains(eventTarget);

        if (isTargetClicked) {
            return;
        }

        const isToggleClicked = toggleElement.contains(eventTarget);

        if (isToggleClicked) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        closeTarget();
    };

    const closeTarget = (): void => {
        for (const dismissElement of dismissElements) {
            dismissElement.removeEventListener(Events.MouseDown, closeTarget);
        }

        switch (triggerEvent) {
            case TriggerEvent.Click:
                targetElement.ownerDocument.removeEventListener(Events.MouseDown, checkOutside);
                break;

            case TriggerEvent.Hover:
                targetElement.ownerDocument.removeEventListener(Events.MouseMove, checkOutside);
                break;
        }

        targetElement.classList.remove(showClassName);
        toggleElement.setAttribute(AriaAttributes.expanded, 'false');

        // Temporary hack to reposition popup:
        document.removeEventListener(onPopupRepositionRequestedEvent, repositionPopup);
    };

    const openTarget = (): void => {
        for (const dismissElement of dismissElements) {
            dismissElement.addEventListener(Events.MouseDown, closeTarget);
        }

        targetElement.classList.add(showClassName);
        toggleElement.setAttribute(AriaAttributes.expanded, 'true');

        setImmediate(() => {
            switch (triggerEvent) {
                case TriggerEvent.Click:
                    targetElement.ownerDocument.addEventListener(Events.MouseDown, checkOutside);
                    break;

                case TriggerEvent.Hover:
                    targetElement.ownerDocument.addEventListener(Events.MouseMove, checkOutside);
                    break;
            }

            repositionPopup();
        });

        // Temporary hack to reposition popup:
        document.addEventListener(onPopupRepositionRequestedEvent, repositionPopup);
    };

    if (!targetElement.classList.contains(showClassName)) {
        openTarget();
    }

    return {
        close: closeTarget
    };
};

export const onPopupRequest = (event: CustomEvent): void => {
    const popupKey = event.detail;
    const targetSelector = `#${popupKey.replace('popups/', 'popups')}`;
    const targetElement = <HTMLElement>document.querySelector(targetSelector);
    const triggerSelector = `[data-target="${targetSelector}"]`;
    const triggerElement = <HTMLElement>document.querySelector(triggerSelector);

    if (targetElement.classList.contains(showClassName)) {
        return;
    }

    const openTargetElement = document.querySelector(`.${showClassName}`);

    if (openTargetElement) {
        openTargetElement.classList.remove(showClassName);
    }

    onShowPopup(triggerElement, targetElement, TriggerEvent.Click);
};

export const onShowModal = (toggleElement: HTMLElement, targetElement: HTMLElement) => {

    if (!toggleElement || !targetElement) {
        return;
    }

    const dismissElement = targetElement.querySelector(`[${DataAttributes.Dismiss}]`);

    const openTarget = (): void => {
        toggleElement.setAttribute(AriaAttributes.expanded, 'true');

        document.body.style.overflow = 'hidden';

        dismissElement?.addEventListener(Events.MouseDown, closeTarget);

        const backdrop: HTMLElement = document.createElement('div');
        backdrop.classList.add('modal__backdrop');

        const content = targetElement.cloneNode(true);

        const host = document.querySelector('.popup-host');
        host.appendChild(backdrop);
        host.appendChild(content);

        setImmediate(() => {
            for (const el of host.children) {
                el.classList.add(showClassName);
            }

            addEventListener(Events.MouseDown, clickOutside);
        });

        const canvas = toggleElement.closest('.grid-container, .parallax-container');
        if (canvas) {
            const canvasVideoButton = canvas.querySelector('.video-dam-button');
            if (canvasVideoButton && canvasVideoButton.getAttribute('data-video-status') !== 'pause') {
                canvas.querySelector<HTMLIFrameElement>('.video-dam-container iframe').contentWindow.postMessage('PAUSE', '*');
                canvasVideoButton.setAttribute('data-video-status', 'hold');
            }
        }

        toggleElement.dispatchEvent(new CustomEvent('modaltoggle', {
            detail: {
                modalOpen: true,
                target: host.querySelector('.modal__content') || host.querySelector('.modal')
            }
        }));
    };

    const closeTarget = (): void => {
        const host = document.querySelector('.popup-host');
        for (const el of host.children) {
            el.classList.remove(showClassName);
        }

        toggleElement.setAttribute(AriaAttributes.expanded, 'false');

        document.body.style.overflow = '';

        setTimeout(() => {
            while (host.lastElementChild) {
                host.removeChild(host.lastElementChild);
            }
        }, modalAnimationDuration);

        removeEventListener(Events.MouseDown, clickOutside);

        dismissElement?.removeEventListener(Events.MouseDown, closeTarget);

        const canvas = toggleElement.closest('.grid-container, .parallax-container');
        if (canvas) {
            const canvasVideoButton = canvas.querySelector('.video-dam-button');
            if (canvasVideoButton && canvasVideoButton.getAttribute('data-video-status') === 'hold') {
                canvas.querySelector<HTMLIFrameElement>('.video-dam-container iframe')?.contentWindow.postMessage('PLAY', '*');
                canvasVideoButton.setAttribute('data-video-status', 'play');
            }
        }

        toggleElement.dispatchEvent(new CustomEvent('modaltoggle', {
            detail: {
                modalOpen: false,
                target: targetElement
            }
        }));
    };

    const clickOutside = (event: MouseEvent) => {
        const clickTarget = <HTMLElement>event.target;

        if (clickTarget.nodeName === 'BODY') {
            return;
        }

        const isTargetClicked = !clickTarget.closest(`[${DataAttributes.Dismiss}]`)
            && (document.querySelector('.popup-host .modal__content') || document.querySelector('.popup-host .modal')).contains(clickTarget);

        if (isTargetClicked) {
            return;
        }

        closeTarget();
    };

    if (targetElement.classList.contains(showClassName)) {
        closeTarget();
    } else {
        openTarget();
    }

    const togglableHandle: ToggleableHandle = {
        close: closeTarget
    };

    return togglableHandle;
};

const damVideoHandler = (e: { data: string }): void => {
    let data: { event: string, playerId: string };
    try {
        data = JSON.parse(e.data);
    } catch {
        return;
    }
    switch (data.event) {
        // case 'ds-player-pause':
        case 'ds-player-ready': {
            const player: HTMLIFrameElement = document.querySelector<HTMLIFrameElement>(`.modal__content.video__modal .video-dam-container #${data.playerId}`);
            player?.contentWindow.postMessage('PLAY', '*');
            break;
        }
        case 'ds-player-play': {
            console.info(`Player ${data.playerId} started playing`);
            /*
            for (const player of document.querySelectorAll<HTMLIFrameElement>('iframe[id^="ds-"]')) {
                if (player.id !== data.playerId) {
                    player.contentWindow.postMessage('PAUSE', '*');
                }
            }
            */
            break;
        }
    }
};

const videoResponsiveMuteHandler = (): void => {
    for (const element of document.getElementsByClassName('video-dam-responsive')) {
        if (window.getComputedStyle(element).display === 'none') {
            const player = element.querySelector<HTMLIFrameElement>('iframe');
            player?.contentWindow.postMessage('MUTE', '*');
        }
    }
};

addEventListener(Events.Click, onClick, true);
addEventListener(Events.KeyDown, onKeyDown, true);
document.documentElement.addEventListener(Events.MouseEnter, onMouseEnter, true);
document.addEventListener(onPopupRequestedEvent, onPopupRequest);
window.addEventListener('message', damVideoHandler);
window.addEventListener('resize', videoResponsiveMuteHandler);
window.addEventListener('orientationchange', videoResponsiveMuteHandler);